import type DolfinFlowExecutor from '../flow-executor/base'
import type DolfinCompensationRulesFlowExecutor from '../flow-executor/compensation-rules'
import { CustomCheckNode } from './action'
import { AdditionNode } from './addition'
import { DolfinNode } from './base'
import { BonusNode } from './bonus'
import { BranchByUserNode } from './branch-by-user'
import { CommissionNode } from './commission'
import { FilterNode } from './filter'
import { IfElseNode } from './if-else'
import { MultiPathNode } from './multi-path'
import { MultiplierNode } from './multiplier'
import { PaymentNode } from './payment'
import { StartNode } from './start'
import {
  AnyTypedNode,
  isAdditionNode,
  isActionNode,
  isBonusNode,
  isBranchByUserNode,
  isCommissionNode,
  isFilterNode,
  isIfElseNode,
  isMultiPathNode,
  isMultiplierNode,
  isPaymentNode,
  isPlaceholderNode,
  isStartNode
} from './types'

function isCompRuleExecutor(e: DolfinFlowExecutor): e is DolfinCompensationRulesFlowExecutor {
  return (e as any).kind === 'comp-rules'
}

export class DolfinNodeFactory {
  static create({
    node,
    flowExecutor
  }: {
    node: AnyTypedNode
    flowExecutor: DolfinFlowExecutor
  }): DolfinNode<DolfinFlowExecutor, AnyTypedNode['type']> {
    if (!node.type) {
      throw new Error('Node type is required')
    }

    if (isFilterNode(node)) {
      return new FilterNode(node, flowExecutor)
    }
    if (isIfElseNode(node)) {
      return new IfElseNode(node, flowExecutor)
    }
    if (isMultiPathNode(node)) {
      return new MultiPathNode(node, flowExecutor)
    }
    if (isBranchByUserNode(node)) {
      return new BranchByUserNode(node, flowExecutor)
    }
    if (isStartNode(node)) {
      return new StartNode(node, flowExecutor)
    }
    if (isActionNode(node)) {
      return new CustomCheckNode(node, flowExecutor)
    }

    if (isCommissionNode(node)) {
      if (!isCompRuleExecutor(flowExecutor)) {
        throw new Error('Commission node can only be used in compensation rules')
      }

      return new CommissionNode(node, flowExecutor)
    }
    if (isBonusNode(node)) {
      if (!isCompRuleExecutor(flowExecutor)) {
        throw new Error('Bonus node can only be used in compensation rules')
      }

      return new BonusNode(node, flowExecutor)
    }
    if (isPaymentNode(node)) {
      if (!isCompRuleExecutor(flowExecutor)) {
        throw new Error('Payment node can only be used in compensation rules')
      }
      return new PaymentNode(node, flowExecutor)
    }
    if (isMultiplierNode(node)) {
      if (!isCompRuleExecutor(flowExecutor)) {
        throw new Error('Multiplier node can only be used in compensation rules')
      }
      return new MultiplierNode(node, flowExecutor)
    }
    if (isAdditionNode(node)) {
      if (!isCompRuleExecutor(flowExecutor)) {
        throw new Error('Addition node can only be used in compensation rules')
      }
      return new AdditionNode(node, flowExecutor)
    }

    throw new Error(`Node type ${node.type} is not supported`)
  }

  static createStaticNode(node: AnyTypedNode): {
    validate: typeof DolfinNode.validate
  } {
    if (isStartNode(node)) {
      return StartNode
    }
    if (isFilterNode(node)) {
      return FilterNode
    }
    if (isIfElseNode(node)) {
      return IfElseNode
    }
    if (isMultiPathNode(node)) {
      return MultiPathNode
    }
    if (isBranchByUserNode(node)) {
      return BranchByUserNode
    }
    if (isActionNode(node)) {
      return CustomCheckNode
    }
    if (isCommissionNode(node)) {
      return CommissionNode
    }
    if (isBonusNode(node)) {
      return BonusNode
    }
    if (isPaymentNode(node)) {
      return PaymentNode
    }
    if (isMultiplierNode(node)) {
      return MultiplierNode
    }
    if (isAdditionNode(node)) {
      return AdditionNode
    }

    if (isPlaceholderNode(node)) {
      return {
        validate: () => ({ errors: [], warnings: [] })
      }
    }

    throw new Error(`Node type ${node.type} is not supported`)
  }
}
